import { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useOutsideClick } from '../../hooks'
import { deleteSubmission } from '../../store/submission.slice'
import { parseDate, sortByDateQuestion, toggleExpandContextMenu } from '../../utils/functions'
import { FormSchema, QuestionSchema } from '../../utils/types/form'
import { SubmissionSchema } from '../../utils/types/submission'

export const SubmissionsTab: React.FC<{
  form: FormSchema | null
  submissions: SubmissionSchema[]
  allQuestions: QuestionSchema[]
  requestState: { info: string; error: string }
  setRequestState: React.Dispatch<
    React.SetStateAction<{
      info: string
      error: string
    }>
  >
}> = (props) => {
  const { form, submissions, allQuestions, requestState, setRequestState } = props

  const [isSubmissionOptionsOpen, setIsSubmissionOptionsOpen] = useState(false)
  const [sortedSubmissions, setSortedSubmissions] = useState<SubmissionSchema[]>([])
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionSchema | null>(null)
  const [dateQuestions, setDateQuestions] = useState<QuestionSchema[]>([])
  const [dateQuestion, setDateQuestion] = useState<QuestionSchema | null>(null)
  const [plotDateQuestionId, setPlotDateQuestionId] = useState('by_date_of_submission')
  const [dateAnswers, setDateAnswers] = useState<number[]>([])
  const [isContextMenuExpanded, setIsContextMenuExpanded] = useState<Record<string, boolean>>({})
  const submissionOptionsTriggerRef = useRef(null)
  const submissionOptionsContextRef = useRef(null)
  const submissionsListRef = useRef<Array<HTMLLIElement | null>>([])
  const selectedSubmissionTriggerRef = useRef(null)
  const selectedSubmissionContextRef = useRef(null)

  useOutsideClick(
    {
      element: submissionOptionsContextRef,
      trigger: submissionOptionsTriggerRef
    },
    () => setIsSubmissionOptionsOpen(false)
  )

  useOutsideClick(
    {
      element: selectedSubmissionContextRef,
      trigger: selectedSubmissionTriggerRef
    },
    () => setSelectedSubmission(null)
  )

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    const plotDateQuestions: QuestionSchema[] = []

    allQuestions.forEach((question) => {
      question.type === 'date' && plotDateQuestions.push(question)
    })

    setDateQuestions(plotDateQuestions)
  }, [allQuestions])

  useEffect(() => {
    let sortedSubmissions: SubmissionSchema[] = []

    if (dateQuestion) {
      sortedSubmissions = sortByDateQuestion(submissions, dateQuestion.id, 'desc')
    } else {
      sortedSubmissions = submissions
    }

    setSortedSubmissions(sortedSubmissions)
  }, [submissions, dateQuestion])

  useEffect(() => {
    if (dateQuestion) {
      const dateAnswers: number[] = []

      sortedSubmissions.forEach(({ sections }, submissionIndex) => {
        sections.forEach(({ questions }) => {
          questions.forEach(({ id, answer }) => {
            if (answer && dateQuestion.id === id) {
              dateAnswers[submissionIndex] = answer as number
            }
          })
        })
      })

      setDateAnswers(dateAnswers)
    }
  }, [sortedSubmissions, dateQuestion])

  const handlePlotDateQuestionChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    questionId: string
  ) => {
    if (e.target.checked) {
      setPlotDateQuestionId(questionId)

      const dateQuestion = allQuestions.find(({ id }) => id === questionId)
      setDateQuestion(dateQuestion ?? null)
    }
  }

  const handleSelectSubmission = async (
    e: React.MouseEvent<HTMLSpanElement>,
    submission: SubmissionSchema
  ) => {
    e.stopPropagation()

    selectedSubmission
      ? selectedSubmission._id === submission._id
        ? setSelectedSubmission(null)
        : setSelectedSubmission(submission)
      : setSelectedSubmission(submission)
  }

  const handleDeleteSubmission = async (e: React.MouseEvent<HTMLSpanElement>, index: number) => {
    e.stopPropagation()

    if (
      window.confirm(
        `Confirm delete: ${form?.isPublic ? 'Response' : 'Entry'} ${sortedSubmissions.length - index}`
      )
    ) {
      requestState.error && setRequestState({ ...requestState, error: '' })

      if (form && selectedSubmission) {
        try {
          submissionsListRef.current[index]?.classList.add('disabled', 'disabled-no-fade')
          const submissionId = selectedSubmission._id
          setSelectedSubmission(null)

          await dispatch(deleteSubmission({ formId: form._id, submissionId })).unwrap()
        } catch (error: any) {
          setRequestState({ ...requestState, error: error.message })
          submissionsListRef.current[index]?.classList.remove('disabled', 'disabled-no-fade')
        }
      }
    }
  }

  const handleExpandContextMenu = (ref: HTMLDivElement) => {
    toggleExpandContextMenu(ref, isContextMenuExpanded, setIsContextMenuExpanded)
  }

  return (
    <>
      {form && (
        <>
          <div className='title_container'>
            <h2>View all {form.isPublic ? 'responses' : 'entries'}</h2>
          </div>
          {!!submissions.length ? (
            <>
              <div className='actions_container'>
                <div className='actions-select'>
                  <span className='material-symbols-rounded'>sort</span>
                  <div className='actions_series'>
                    <div>
                      <div
                        id='plot_date_questions_trigger'
                        className='div-with-icon'
                        onClick={() => setIsSubmissionOptionsOpen(!isSubmissionOptionsOpen)}
                        ref={submissionOptionsTriggerRef}>
                        <span id='plot_date_questions_trigger'>Select dates</span>
                        <span id='plot_date_questions_trigger' className='material-symbols-rounded'>
                          {isSubmissionOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                        </span>
                      </div>
                      {isSubmissionOptionsOpen && (
                        <div
                          className={`context_menu context_menu-left context_menu-down context_menu-${
                            isContextMenuExpanded['submissionOptionsContext'] ? 'lg' : 'md'
                          }`}
                          id='submissionOptionsContext'
                          ref={submissionOptionsContextRef}>
                          <div className='context_menu-grid context-header'>
                            <div
                              onClick={() =>
                                submissionOptionsContextRef.current &&
                                handleExpandContextMenu(submissionOptionsContextRef.current)
                              }>
                              <h4>Dates</h4>
                              <span className='material-symbols-rounded'>
                                {isContextMenuExpanded['submissionOptionsContext']
                                  ? 'collapse_content'
                                  : 'expand_content'}
                              </span>
                            </div>
                            <input
                              id='checkbox_by_submitted'
                              type='checkbox'
                              checked={plotDateQuestionId === 'by_date_of_submission'}
                              onChange={(e) =>
                                handlePlotDateQuestionChange(e, 'by_date_of_submission')
                              }
                            />
                            <label
                              htmlFor='checkbox_by_submitted'
                              className='highlight-styled-hover'>
                              By date submitted
                            </label>
                          </div>
                          {!!dateQuestions.length && (
                            <>
                              {form?.sections.map(({ id: sectionId, name, questions }) => {
                                const hasQuestions = questions.some(({ type }) => type === 'date')

                                if (!hasQuestions) {
                                  return <Fragment key={sectionId} />
                                }

                                return (
                                  <div className='context_menu-grouped' key={sectionId}>
                                    <h5>{name}</h5>
                                    <div className='context_menu-grid'>
                                      {questions.map(({ id: questionId, statement, type }) => {
                                        if (type === 'date') {
                                          return (
                                            <Fragment key={questionId}>
                                              <input
                                                id={`checkbox_${questionId}`}
                                                type='checkbox'
                                                checked={plotDateQuestionId === questionId}
                                                onChange={(e) =>
                                                  handlePlotDateQuestionChange(e, questionId)
                                                }
                                              />
                                              <label
                                                htmlFor={`checkbox_${questionId}`}
                                                className='highlight-styled-hover'>
                                                {statement}
                                              </label>
                                            </Fragment>
                                          )
                                        } else {
                                          return <Fragment key={questionId} />
                                        }
                                      })}
                                    </div>
                                  </div>
                                )
                              })}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {dateQuestion ? (
                    <span
                      className='actions-active'
                      title={`${dateQuestion.statement}${
                        dateQuestion.description ? ' (' + dateQuestion.description + ')' : ''
                      }`}>
                      {dateQuestion.statement}
                    </span>
                  ) : (
                    <span className='actions-active'>By date submitted</span>
                  )}
                </div>
              </div>
              <ul className='submission-list'>
                <li>
                  <div />
                  <h3>{`${form.isPublic ? 'Responses' : 'Entries'}`}</h3>
                  <h3>Dates</h3>
                  <div />
                </li>
                {sortedSubmissions.map((submission, index) => (
                  <li
                    onClick={() =>
                      navigate(`/form/${submission.formId}/submission/${submission._id}`, {
                        viewTransition: true
                      })
                    }
                    key={submission._id}
                    ref={(el) => {
                      submissionsListRef.current[index] = el
                    }}>
                    <span className='material-symbols-rounded'>file_open</span>
                    <span>{`${form.isPublic ? 'Response' : 'Entry'} ${sortedSubmissions.length - index}`}</span>
                    <span>
                      {plotDateQuestionId === dateQuestion?.id ? (
                        <>{parseDate(dateAnswers[index], 'short_date')}</>
                      ) : (
                        <>{parseDate(submission.submittedOn, 'short_date')}</>
                      )}
                    </span>
                    <div className='options'>
                      <span
                        id='options_trigger'
                        className='material-symbols-rounded options_trigger'
                        onClick={(e) => handleSelectSubmission(e, submission)}
                        ref={selectedSubmissionTriggerRef}>
                        more_vert
                      </span>
                      {selectedSubmission?._id === submission._id && (
                        <div
                          className='context_menu context_menu-right context_menu-wrap'
                          ref={selectedSubmissionContextRef}>
                          <div
                            className='div-with-icon error'
                            onClick={(e) => handleDeleteSubmission(e, index)}>
                            <span className='material-symbols-rounded'>delete</span>
                            <span>Delete {form.isPublic ? 'response' : 'entry'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <span>No {form.isPublic ? 'responses' : 'entries'} yet...</span>
          )}
        </>
      )}
    </>
  )
}
